import React, { useState, useEffect, useRef } from "react";

const overlayClass = "fixed inset-0 z-[9999] bg-transparent";

const popupClass =
  "absolute z-[10000] w-[250px] -translate-x-1/2 rounded-lg border border-[#e0e0e0] bg-white p-4 font-sans shadow-[0_4px_12px_rgba(0,0,0,0.15)] outline-none max-[480px]:w-[220px] max-[480px]:p-3";

const titleClass = "mb-3 text-sm font-semibold leading-tight text-[#333]";

const controlSectionClass =
  "mb-3 flex items-center gap-3 max-[480px]:flex-col max-[480px]:items-stretch max-[480px]:gap-2";

const sliderWrapClass = "relative flex h-5 flex-1 items-center";

const rangeClass =
  "relative z-[2] h-1 w-full cursor-pointer appearance-none rounded-sm bg-[#e0e0e0] outline-none " +
  "[&::-webkit-slider-thumb]:z-[3] [&::-webkit-slider-thumb]:h-4 [&::-webkit-slider-thumb]:w-4 [&::-webkit-slider-thumb]:cursor-pointer [&::-webkit-slider-thumb]:appearance-none [&::-webkit-slider-thumb]:rounded-full [&::-webkit-slider-thumb]:border-2 [&::-webkit-slider-thumb]:border-white [&::-webkit-slider-thumb]:bg-[#4a90e2] [&::-webkit-slider-thumb]:shadow-[0_2px_4px_rgba(0,0,0,0.2)] " +
  "[&::-moz-range-thumb]:z-[3] [&::-moz-range-thumb]:h-4 [&::-moz-range-thumb]:w-4 [&::-moz-range-thumb]:cursor-pointer [&::-moz-range-thumb]:rounded-full [&::-moz-range-thumb]:border-2 [&::-moz-range-thumb]:border-white [&::-moz-range-thumb]:bg-[#4a90e2] [&::-moz-range-thumb]:shadow-[0_2px_4px_rgba(0,0,0,0.2)]";

const ticksClass =
  "pointer-events-none absolute left-0 right-0 top-1/2 z-[1] h-1 -translate-y-1/2";

const tickClass = "absolute top-0 h-1 w-px -translate-x-1/2 bg-[#e0e0e0]";

const inputRowClass = "flex items-center gap-1 max-[480px]:justify-center";

const speedInputClass =
  "h-6 w-[50px] rounded border border-[#e0e0e0] bg-white px-1.5 text-center text-xs text-[#333] focus:border-[#4a90e2] focus:outline-none";

const spinnerClass =
  "flex h-6 w-5 flex-col overflow-hidden rounded-r border border-l-0 border-[#e0e0e0]";

const spinnerBtnClass =
  "flex flex-1 items-center justify-center border-none bg-white p-0 text-[8px] leading-none text-[#333] transition-colors duration-200 hover:enabled:bg-[#f5f5f5] active:enabled:bg-[#e8e8e8] disabled:cursor-not-allowed disabled:opacity-50";

const spinnerUpClass = `${spinnerBtnClass} border-b border-[#e0e0e0]`;

interface SpeedAdjustmentPopupProps {
  visible: boolean;
  onClose: () => void;
  onSpeedChange: (speed: number) => void;
  initialSpeed?: number;
  position?: {
    top: number;
    left: number;
  };
  resetTrigger?: number;
}

const SpeedAdjustmentPopup: React.FC<SpeedAdjustmentPopupProps> = ({
  visible,
  onClose,
  onSpeedChange,
  initialSpeed = 1.0,
  position = { top: 0, left: 0 },
  resetTrigger,
}) => {
  const [speed, setSpeed] = useState(initialSpeed);
  const debounceTimer = useRef<NodeJS.Timeout | null>(null);

  useEffect(() => {
    setSpeed(initialSpeed);
  }, [initialSpeed]);

  useEffect(() => {
    if (resetTrigger !== undefined) {
      setSpeed(initialSpeed);
    }
  }, [resetTrigger, initialSpeed]);

  useEffect(() => {
    return () => {
      if (debounceTimer.current) {
        clearTimeout(debounceTimer.current);
      }
    };
  }, []);

  const handleSpeedChange = (newSpeed: number) => {
    const clampedSpeed = Math.max(0.5, Math.min(2.0, newSpeed));
    setSpeed(clampedSpeed);

    if (debounceTimer.current) {
      clearTimeout(debounceTimer.current);
    }

    debounceTimer.current = setTimeout(() => {
      onSpeedChange(clampedSpeed);
    }, 300);
  };

  const handleSliderChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    handleSpeedChange(parseFloat(e.target.value));
  };

  const handleInputChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const numericValue = e.target.value.replace("x", "");
    const newSpeed = parseFloat(numericValue);

    if (!isNaN(newSpeed)) {
      handleSpeedChange(newSpeed);
    }
  };

  const handleKeyDown = (e: React.KeyboardEvent) => {
    if (e.key === "Escape") {
      onClose();
    }
  };

  if (!visible) return null;

  return (
    <div className={overlayClass} onClick={onClose}>
      <div
        className={popupClass}
        style={{ top: position.top, left: position.left }}
        onClick={(e) => e.stopPropagation()}
        onKeyDown={handleKeyDown}
        tabIndex={-1}
      >
        <div className={titleClass}>调节语速</div>

        <div className={controlSectionClass}>
          <div className={sliderWrapClass}>
            <input
              type="range"
              min="0.5"
              max="2.0"
              step="0.1"
              value={speed}
              onChange={handleSliderChange}
              className={rangeClass}
            />
            <div className={ticksClass}>
              <div className={tickClass} style={{ left: "0%" }} />
              <div className={tickClass} style={{ left: "33.33%" }} />
              <div className={tickClass} style={{ left: "66.66%" }} />
              <div className={tickClass} style={{ left: "100%" }} />
            </div>
          </div>

          <div className={inputRowClass}>
            <input type="text" value={`${speed.toFixed(1)}x`} onChange={handleInputChange} className={speedInputClass} />
            <div className={spinnerClass}>
              <button
                type="button"
                className={spinnerUpClass}
                onClick={() => handleSpeedChange(speed + 0.1)}
                disabled={speed >= 2.0}
              >
                ▲
              </button>
              <button
                type="button"
                className={spinnerBtnClass}
                onClick={() => handleSpeedChange(speed - 0.1)}
                disabled={speed <= 0.5}
              >
                ▼
              </button>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
};

export default SpeedAdjustmentPopup;
